'use client';

import { useEffect } from 'react';

// In production, intercepts all console.log/info/warn/error calls in the
// browser: they are batched and shipped to /api/log (which prints them to
// server stdout → Vector → VictoriaLogs), and are NOT printed to the
// visitor's devtools console — the client stays silent, checks happen in the
// server logs UI. Uncaught errors and unhandled promise rejections are
// forwarded too. In development this component does nothing.

type Level = 'log' | 'info' | 'warn' | 'error';

interface Entry {
  level: Level;
  msg: string;
  page: string;
}

const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE = 100;
const MAX_MSG_LEN = 2000;
const LEVELS: Level[] = ['log', 'info', 'warn', 'error'];

function serialize(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === 'string') return a;
      if (a instanceof Error) return `${a.name}: ${a.message}`;
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    })
    .join(' ')
    .slice(0, MAX_MSG_LEN);
}

export default function BrowserLogForwarder() {
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    const w = window as typeof window & { __logForwarder?: boolean };
    if (w.__logForwarder) return;
    w.__logForwarder = true;

    let queue: Entry[] = [];

    const push = (level: Level, args: unknown[]) => {
      if (queue.length >= MAX_QUEUE) return;
      queue.push({
        level,
        msg: serialize(args),
        page: window.location.pathname,
      });
    };

    const flush = (useBeacon = false) => {
      if (queue.length === 0) return;
      const payload = JSON.stringify({ entries: queue });
      queue = [];
      if (useBeacon && navigator.sendBeacon) {
        navigator.sendBeacon(
          '/api/log',
          new Blob([payload], { type: 'application/json' })
        );
      } else {
        fetch('/api/log', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true,
        }).catch(() => {});
      }
    };

    // Silence the console in production: capture and forward instead of
    // printing, so visitors' devtools stay clean.
    for (const level of LEVELS) {
      console[level] = (...args: unknown[]) => {
        push(level, args);
      };
    }

    const onError = (event: ErrorEvent) =>
      push('error', [
        `Uncaught: ${event.message} (${event.filename}:${event.lineno})`,
      ]);
    const onRejection = (event: PromiseRejectionEvent) =>
      push('error', [`Unhandled rejection: ${serialize([event.reason])}`]);
    const onHide = () => {
      if (document.visibilityState === 'hidden') flush(true);
    };

    const timer = setInterval(() => flush(), FLUSH_INTERVAL_MS);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    window.addEventListener('pagehide', () => flush(true));
    document.addEventListener('visibilitychange', onHide);

    return () => clearInterval(timer);
  }, []);

  return null;
}
